import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Footer from '@/components/Footer';
import { BrandCard } from '@/components/brands/BrandCard';

const fetcher = (url) => api.get(url).then((res) => res.data);

export default function Brands({ user }) {
  const darkMode = useDarkMode();
  const isDark = darkMode.isDark;
  const { data: brands = [], isLoading } = useSWR('/brands', fetcher);

  useDocumentMeta({
    title: 'Support a Brand — Mementa',
    description: 'Discover and support small businesses, makers, and indie brands worth knowing.',
    url: 'https://mementa.gifts/brands',
  });

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      {/* Hero */}
      <div
        className="pt-16 pb-12 px-4"
        style={{
          background: isDark
            ? 'radial-gradient(ellipse at center top, rgba(212,164,168,0.18) 0%, transparent 65%)'
            : 'radial-gradient(ellipse at center top, rgba(83,65,69,0.12) 0%, transparent 65%)',
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold mb-4 px-3 py-1 rounded-full"
            style={{
              color: isDark ? '#D4A4A8' : '#3F2F31',
              background: isDark ? 'rgba(212,164,168,0.15)' : 'rgba(83,65,69,0.08)',
              border: `1px solid ${isDark ? 'rgba(212,164,168,0.4)' : 'rgba(83,65,69,0.25)'}`,
            }}
          >
            <Sparkles size={12} />
            Small Businesses
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: isDark ? '#FFFAF5' : '#20212B',
            }}
            data-testid="brands-page-title"
          >
            Support a Brand
          </h1>
          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-8"
            style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)' }}
          >
            A home for the makers, founders, and indie brands worth knowing. Every visit sends shoppers straight to their store.
          </p>
          <Link
            to="/brands/apply"
            data-testid="brands-apply-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
            style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
          >
            Apply to be featured
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Directory grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ aspectRatio: '4 / 5', background: isDark ? '#1E2A36' : '#F7F0EA' }} />
            ))}
          </div>
        ) : brands.length === 0 ? (
          <div
            data-testid="brands-empty-state"
            className="rounded-2xl p-10 sm:p-16 text-center max-w-2xl mx-auto"
            style={{
              background: isDark ? 'rgba(212,164,168,0.08)' : 'rgba(83,65,69,0.05)',
              border: `1px dashed ${isDark ? 'rgba(212,164,168,0.4)' : 'rgba(83,65,69,0.3)'}`,
            }}
          >
            <h2
              className="mb-3"
              style={{
                fontFamily: "'Bodoni Moda', serif",
                fontWeight: 700,
                fontSize: '24px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: isDark ? '#FFFAF5' : '#20212B',
              }}
            >
              The directory is just opening
            </h2>
            <p className="text-sm mb-6" style={{ color: isDark ? 'rgba(255,250,245,0.7)' : 'rgba(83,65,69,0.85)' }}>
              We're hand-reviewing the first wave of small businesses. Own a brand? Be one of the founding names here.
            </p>
            <Link
              to="/brands/apply"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
              style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
            >
              Apply to be featured
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="brands-grid">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} isDark={isDark} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
